/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { AsgardeoSPAClient, Hooks } from "@asgardeo/auth-react";

import Button from "@material-ui/core/Button";
import clsx from 'clsx';
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import { Typography, Box, Divider } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import classNames from "classnames";

import { useStyles } from "./style";
import {
  RESOURCE_SIGN_IN,
  FIDP,
  SELECTED_ORG_HANDLE_NAME,
  FORBIDDEN_SELECTED_ORG_HANDLE_NAME,
} from "../../data/constants";
import { isSelfSignupEnabled, localStoreOperation } from "../../data/utils";
import { StorageOperations } from "../../data/interfaces";
import DevPortalFooter from "../shared/DevPortalFooter";
import Settings from "../../conf/settings";
import EnterpriseLoginBox from "../../elements/EnterpriseLoginBox";
import LoginCard from "./loginCard";

function Login() {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const authClient = AsgardeoSPAClient.getInstance();
  const [enableEnterpriseLogin, setEnableEnterpriseLogin] = useState(false);

  useEffect(() => {
    // Removing localStore fidp value on sigin out
    authClient?.on(Hooks.SignOut, () => {
      const orgName =
        localStoreOperation(StorageOperations.GET, SELECTED_ORG_HANDLE_NAME) ||
        "";
      localStoreOperation(StorageOperations.REMOVE, FIDP);
      localStoreOperation(StorageOperations.REMOVE, SELECTED_ORG_HANDLE_NAME);
      if (isSelfSignupEnabled(orgName)) {
        history.push(`/${orgName}`);
      }
      const forbiddenOrgName = localStoreOperation(StorageOperations.GET, FORBIDDEN_SELECTED_ORG_HANDLE_NAME)
      if (forbiddenOrgName){
        history.replace(forbiddenOrgName);
        localStoreOperation(StorageOperations.REMOVE, FORBIDDEN_SELECTED_ORG_HANDLE_NAME);
      }
    });
  }, []);

  const handleGithubLogin = () => {
    localStoreOperation(StorageOperations.ADD, FIDP, Settings.githubFidp);
    history.push(RESOURCE_SIGN_IN);
  };

  const handleGoogleLogin = () => {
    localStoreOperation(StorageOperations.ADD, FIDP, Settings.googleFidp);
    history.push(RESOURCE_SIGN_IN);
  };

  const handleMicrosoftLogin = () => {
    localStoreOperation(StorageOperations.ADD, FIDP, Settings.microsoftFidp);
    history.push(RESOURCE_SIGN_IN);
  };

  const handleEmailLogin = () => {
    localStoreOperation(StorageOperations.ADD, FIDP, Settings.emailFidp);
    history.push(RESOURCE_SIGN_IN);
  };

  const handleEnterpriseLogin = (userEmail: string) => {
    localStoreOperation(StorageOperations.ADD, FIDP, Settings.enterpriseFidp);
    history.push(RESOURCE_SIGN_IN, { enterpriseEmail: userEmail});
  };

  const defaultMsg = (
    <span>
      <Typography className={classes.successMsg}>
        <FormattedMessage
          id="home.login.messages.signInMessage"
          defaultMessage="If you already have an account, please sign in"
        />
      </Typography>
    </span>
  );

  return (
    <React.Fragment>
      <div className={classes.loginBg}>
        <Grid
          container={true}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item={true} xs={12} md={9}>
            <Container fixed={true} classes={{ root: classes.container }}>
              <Grid
                container={true}
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
              >
                <Grid
                  item={true}
                  xs={12}
                  lg={7}
                  className={classes.contentContainer}
                >
                  <Grid item={true} xs={12}>
                    <Box display="flex" justifyContent="flex-start">
                      <img
                        src={
                          theme.custom.login.logo.src ??
                          "/themes/default/images/logo-white.svg"
                        }
                        className={classes.logo}
                      />
                      <Divider
                        orientation="vertical"
                        flexItem
                        className={classes.divider}
                      />
                      <Typography className={classes.logoTitle}>
                        <FormattedMessage
                          id="home.login.labels.title.devportal"
                          defaultMessage="API Developer Portal"
                        />
                      </Typography>
                    </Box>
                    <Grid item={true} xs={12} sm={10} md={10}>
                      <div className={classes.msgContainer}>{defaultMsg}</div>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item={true} xs={12} lg={5} className={classes.box}>
                  <Grid container={true} direction="row">
                    <Grid item={true} xs={12}>
                      <div className={classes.loginWrapper}>
                        <div className={classes.tagLineContainer}>
                          <Typography
                            className={classes.tagLine}
                            component="div"
                          >
                            <FormattedMessage
                              id="home.login.labels.title.signIn"
                              defaultMessage="Sign In to"
                            />
                          </Typography>
                          <Typography
                            className={classes.tagLine}
                            component="div"
                          >
                            <FormattedMessage
                              id="home.login.labels.title.apiDevportal"
                              defaultMessage="API Developer Portal"
                            />
                          </Typography>
                        </div>
                        <div className={classes.btnContainer}>
                          {
                            enableEnterpriseLogin ? 
                              <EnterpriseLoginBox
                                setEnableEnterpriseLogin={setEnableEnterpriseLogin}
                                handleEnterpriseLoginClick={handleEnterpriseLogin}
                              />
                              :
                              <>
                                <LoginCard
                                    handleEmailLogin={handleEmailLogin}
                                    handleGithubLogin={handleGithubLogin}
                                    handleGoogleLogin={handleGoogleLogin}
                                    handleMicrosoftLogin={handleMicrosoftLogin}
                                />
                                <Button
                                  className={classNames(classes.loginBtn, classes.enterprise)}
                                  variant="contained"
                                  onClick={() => setEnableEnterpriseLogin(true)}
                                  aria-label="Enterprise ID sign in"
                                  data-cyid="sign-in-with-enterprise"
                                >
                                    <img
                                        src="/themes/default/images/enterprise.svg"
                                        className={clsx(
                                            classes.enterpriseBtnIcon,
                                            { [classes.enterpriseAlign]: Settings.enableEmailLogin })
                                        }
                                        alt='Enterprise Logo'
                                    />
                                    <FormattedMessage
                                        id="home.login.buttons.signIn.enterprise"
                                        defaultMessage="Sign in with Enterprise ID"
                                    />
                                </Button>
                              </>
                          }
                        </div>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </Grid>
        </Grid>
      </div>
      <DevPortalFooter />
    </React.Fragment>
  );
}

export default Login;
